import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { Check, ChevronDown, Clock, Gift, Heart, Share2, Shield, Star, UserCheck, Users, Zap } from 'lucide-react';

const faqItems = [
  {
    question: 'Comment fonctionne le programme de parrainage ?',
    answer:
      "Partagez votre code unique avec d'autres organisateurs. Quand votre filleul génère sa première commission réelle (premier concours avec inscriptions payées), vous recevez chacun 30 € de crédit de commission appliqué automatiquement sur vos prochaines commissions.",
  },
  {
    question: 'Combien de filleuls puis-je parrainer ?',
    answer:
      "Vous pouvez parrainer autant d'organisateurs que vous le souhaitez, mais seuls 5 filleuls qualifiés ouvrent droit à récompense, soit 150 € de crédit de commission au maximum. Au-delà de 5 filleuls validés, les parrainages supplémentaires n'ouvrent pas droit à crédit.",
  },
  {
    question: 'Quand est-ce que mon crédit est activé ?',
    answer:
      "Votre crédit de 30 € est activé dès que votre filleul génère sa première commission réelle — c'est-à-dire son premier concours avec des inscriptions payantes. Vous recevez une notification par email dans les 24h.",
  },
  {
    question: 'Comment le crédit de commission est-il utilisé ?',
    answer:
      "Le crédit est appliqué automatiquement sur vos prochaines commissions BoulesConnect. Il réduit la commission de 2 % prélevée sur les inscriptions de vos concours jusqu'à épuisement du solde. Aucune action manuelle n'est nécessaire.",
  },
  {
    question: 'Le crédit de commission expire-t-il ?',
    answer:
      "Non. Votre crédit ne expire pas tant que votre compte est actif sur BoulesConnect. Il reste disponible et s'applique automatiquement à chaque concours jusqu'à épuisement.",
  },
  {
    question: "Que se passe-t-il si mon filleul arrête d'utiliser BoulesConnect ?",
    answer:
      'Une fois votre crédit de 30 € crédité, il reste définitivement acquis même si votre filleul cesse son activité. La récompense est définitive dès validation de la première commission.',
  },
];

const conditions = [
  {
    icon: Shield,
    color: 'text-blue-600 bg-blue-100',
    text: 'Le filleul doit être un nouvel utilisateur BoulesConnect',
  },
  {
    icon: Clock,
    color: 'text-emerald-600 bg-emerald-100',
    text: 'Le filleul doit générer sa première commission (premier concours avec inscriptions payées)',
  },
  {
    icon: Users,
    color: 'text-purple-600 bg-purple-100',
    text: 'Maximum 5 filleuls qualifiés (150 € de crédit au total)',
  },
  {
    icon: Zap,
    color: 'text-orange-600 bg-orange-100',
    text: 'Le crédit de 30 € est crédité après la première commission générée par le filleul',
  },
  {
    icon: Heart,
    color: 'text-rose-600 bg-rose-100',
    text: 'Le crédit est une remise non remboursable appliquée automatiquement sur les prochaines commissions',
  },
  {
    icon: Star,
    color: 'text-amber-600 bg-amber-100',
    text: 'Programme réservé aux organisateurs actifs sur BoulesConnect',
  },
];

export default function Parrainage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section className='pt-32 pb-20 px-6'>
      <PageMeta
        title='Parrainage organisateurs'
        description="Parrainez des organisateurs de concours sur BoulesConnect et recevez 30 € de crédit de commission par filleul. Jusqu'à 150 € de crédit pour 5 filleuls."
      />
      {/* Hero */}
      <div className='max-w-4xl mx-auto text-center mb-20'>
        <div className='inline-flex items-center gap-2 bg-blue-50 text-blue-700 font-semibold text-sm px-4 py-2 rounded-full mb-6'>
          <Gift className='w-4 h-4' />
          Programme de Parrainage Organisateurs
        </div>
        <h1 className='text-4xl md:text-5xl font-extrabold mb-6'>
          <span className='bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent'>
            Recommandez, Gagnez !
          </span>
        </h1>
        <p className='text-lg text-slate-600 max-w-2xl mx-auto'>
          Parrainez d'autres organisateurs et recevez <strong>30&nbsp;€ de crédit de commission par filleul</strong>,
          jusqu'à <strong>150&nbsp;€ de crédit au total</strong>. Un programme gagnant-gagnant pour toute la communauté
          pétanque.
        </p>
      </div>

      {/* Value Cards */}
      <div className='max-w-5xl mx-auto grid md:grid-cols-2 gap-8 mb-20'>
        {/* Parrain */}
        <div className='bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 text-white'>
          <h3 className='text-2xl font-bold mb-2'>Pour le Parrain</h3>
          <p className='text-blue-200 mb-6'>Vous recommandez, vous gagnez</p>
          <div className='space-y-4'>
            {[
              '30 € de crédit commission par filleul validé',
              "Jusqu'à 5 filleuls (150 € de crédit max)",
              'Crédit appliqué automatiquement sur vos prochaines commissions',
            ].map((benefit, i) => (
              <div key={i} className='flex items-center gap-3'>
                <div className='w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0'>
                  <Check className='w-4 h-4' />
                </div>
                <span className='font-medium'>{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Filleul */}
        <div className='bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-8 text-white'>
          <h3 className='text-2xl font-bold mb-2'>Pour le Filleul</h3>
          <p className='text-emerald-200 mb-6'>Vous rejoignez, vous profitez</p>
          <div className='space-y-4'>
            {[
              '30 € de crédit commission offerts',
              'Appliqué dès votre premier concours payant',
              'Devenez parrain à votre tour',
            ].map((benefit, i) => (
              <div key={i} className='flex items-center gap-3'>
                <div className='w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0'>
                  <Check className='w-4 h-4' />
                </div>
                <span className='font-medium'>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Comment ca marche */}
      <div className='max-w-5xl mx-auto mb-20'>
        <h2 className='text-3xl font-extrabold text-slate-900 text-center mb-12'>Comment ça marche ?</h2>
        <div className='grid md:grid-cols-3 gap-8'>
          {[
            {
              icon: Share2,
              color: 'bg-blue-100 text-blue-600',
              step: '1',
              title: 'Partagez votre code',
              desc: 'Envoyez votre code de parrainage unique à vos contacts organisateurs de concours.',
            },
            {
              icon: UserCheck,
              color: 'bg-emerald-100 text-emerald-600',
              step: '2',
              title: "Votre filleul s'inscrit",
              desc: 'Il crée son compte organisateur et utilise votre code lors de son inscription.',
            },
            {
              icon: Gift,
              color: 'bg-purple-100 text-purple-600',
              step: '3',
              title: 'Gagnez 30 € de crédit',
              desc: 'Dès que votre filleul génère sa première commission (premier concours avec inscriptions payées), 30 € de crédit sont ajoutés à vos deux comptes.',
            },
          ].map((step, i) => (
            <div key={i} className='text-center'>
              <div className='relative inline-flex mb-6'>
                <div className={`w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center`}>
                  <step.icon className='w-8 h-8' />
                </div>
                <span className='absolute -top-2 -right-2 w-7 h-7 bg-slate-900 text-white text-sm font-bold rounded-full flex items-center justify-center'>
                  {step.step}
                </span>
              </div>
              <h3 className='text-lg font-bold text-slate-900 mb-2'>{step.title}</h3>
              <p className='text-slate-600'>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Calculator */}
      <div className='max-w-4xl mx-auto mb-20'>
        <div className='bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-10 text-white'>
          <h2 className='text-2xl font-bold text-center mb-2'>Calculez votre crédit</h2>
          <p className='text-slate-400 text-center mb-10'>Crédit commission — 30&nbsp;€ par filleul validé</p>
          <div className='grid grid-cols-3 gap-6'>
            {[
              { filleuls: 2, savings: '60 €', highlight: false },
              { filleuls: 3, savings: '90 €', highlight: false },
              { filleuls: 5, savings: '150 €', highlight: true },
            ].map((col, i) => (
              <div
                key={i}
                className={`text-center p-6 rounded-2xl ${
                  col.highlight ? 'bg-gradient-to-b from-blue-600 to-indigo-600 ring-2 ring-blue-400' : 'bg-white/10'
                }`}
              >
                <p className='text-sm text-slate-300 mb-1'>{col.filleuls} filleuls</p>
                <p className='text-3xl font-extrabold mb-1'>{col.savings}</p>
                <p className='text-xs text-slate-400'>de crédit commission</p>
                {col.highlight && (
                  <span className='inline-block mt-3 text-xs font-bold bg-white/20 px-3 py-1 rounded-full'>MAX</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Conditions */}
      <div className='max-w-4xl mx-auto mb-20'>
        <h2 className='text-3xl font-extrabold text-slate-900 text-center mb-12'>Conditions du programme</h2>
        <div className='grid md:grid-cols-2 gap-4'>
          {conditions.map((condition, i) => (
            <div key={i} className='flex items-start gap-4 bg-white rounded-2xl p-5 border border-slate-100'>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${condition.color}`}>
                <condition.icon className='w-5 h-5' />
              </div>
              <p className='text-slate-700 text-sm leading-relaxed'>{condition.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className='max-w-3xl mx-auto mb-20'>
        <h2 className='text-3xl font-extrabold text-slate-900 text-center mb-12'>Questions fréquentes</h2>
        <div className='space-y-3'>
          {faqItems.map((item, i) => (
            <details
              key={i}
              open={openFaq === i}
              className='group bg-white border border-slate-200 rounded-2xl overflow-hidden'
            >
              <summary
                onClick={e => {
                  e.preventDefault();
                  setOpenFaq(openFaq === i ? null : i);
                }}
                className='flex items-center justify-between cursor-pointer p-5 font-semibold text-slate-900 hover:bg-slate-50 transition-colors'
              >
                {item.question}
                <ChevronDown
                  className={`w-5 h-5 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                />
              </summary>
              {openFaq === i && <div className='px-5 pb-5 text-slate-600 leading-relaxed'>{item.answer}</div>}
            </details>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className='max-w-4xl mx-auto'>
        <div className='bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-10 text-center text-white'>
          <h2 className='text-3xl font-bold mb-4'>Prêt à parrainer ?</h2>
          <p className='text-blue-100 mb-8 max-w-xl mx-auto'>
            Rejoignez le programme de parrainage et commencez à économiser dès aujourd'hui.
          </p>
          <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
            <Link
              to={ROUTES.REGISTER}
              className='bg-white text-blue-600 font-bold px-8 py-3 rounded-xl hover:bg-blue-50 transition-colors'
            >
              Devenir Organisateur
            </Link>
            <Link
              to={ROUTES.LOGIN}
              className='border-2 border-white/30 text-white font-bold px-8 py-3 rounded-xl hover:bg-white/10 transition-colors'
            >
              J'ai un code parrain
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
